import { Component, EventEmitter, OnInit } from "@angular/core";
import { AbstractControl, FormBuilder, FormGroup, Validators } from "@angular/forms";
import { BsModalRef } from "ngx-bootstrap/modal";
import { InvoiceDetailsModel, InvoiceRefundDetailsModel, InvoiceRefundSubmitModel } from "@model/Invoice.model";

interface InvoiceRefundRow {
    invoiceDetailsId: number;
    accountCode: string;
    description: string;
    total: number;
    remaining: number;
    selected: boolean;
    amount: number | null;
}

@Component({
    templateUrl: "invoice-refund-modal.component.html",
    standalone: false
})
export class InvoiceRefundModalComponent implements OnInit {
    title: string = "Refund Invoice";
    invoiceDetails: InvoiceDetailsModel[] = [];
    refundDetails: InvoiceRefundDetailsModel[] = [];
    submitEvent: EventEmitter<InvoiceRefundSubmitModel> = new EventEmitter();

    rows: InvoiceRefundRow[] = [];
    errorMessage: string = "";
    form: FormGroup;
    submitted: boolean = false;

    modalRef: BsModalRef;
    constructor(
        private bsModalRef: BsModalRef,
        private formBuilder: FormBuilder) {
        this.modalRef = bsModalRef;
    }

    ngOnInit(): void {
        this.form = this.formBuilder.group({
            reason: ['', Validators.required],
        });

        this.rows = (this.invoiceDetails ?? [])
            .filter(item => (item.total ?? 0) > 0)
            .map(item => {
                const alreadyRefunded = (this.refundDetails ?? [])
                    .filter(r => r.invoiceDetailsId === item.id)
                    .reduce((sum, r) => sum + (r.amount ?? 0), 0);

                const remaining = (item.total ?? 0) - alreadyRefunded;

                return {
                    invoiceDetailsId: item.id!,
                    accountCode: item.accountCode ?? "",
                    description: item.description ?? "",
                    total: item.total ?? 0,
                    remaining,
                    selected: false,
                    amount: null,
                } as InvoiceRefundRow;
            })
            .filter(row => row.remaining > 0);
    }

    onSelectChange(row: InvoiceRefundRow) {
        if (row.selected && !row.amount) {
            row.amount = row.remaining;
        }

        if (!row.selected) {
            row.amount = null;
        }
    }

    onSubmit() {
        this.errorMessage = "";
        this.submitted = true;
        const selectedRows = this.rows.filter(r => r.selected);

        if (selectedRows.length === 0) {
            this.errorMessage = "Please select at least one item to refund";
            return;
        }

        for (const row of selectedRows) {
            if (!row.amount || row.amount <= 0) {
                this.errorMessage = `Please enter a valid refund amount for ${row.description}`;
                return;
            }

            if (row.amount > row.remaining) {
                this.errorMessage = `Refund amount for ${row.description} cannot be more than ${row.remaining.toFixed(2)}`;
                return;
            }
        }

        if (this.form.invalid) {
            return;
        }

        this.modalRef.hide();
        this.submitEvent.emit({
            reason: this.form.get('reason')!.value,
            details: selectedRows.map(r => ({
                invoiceDetailsId: r.invoiceDetailsId,
                amount: r.amount!,
            })),
        });
    }

    get f(): { [key: string]: AbstractControl; } {
        return this.form.controls;
    }
}
